import React, { useState, ReactNode, MouseEvent, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useHover from "src/utils/react-use-hover";
import { twMerge } from "tailwind-merge";


interface TooltipProps {
  text: string;
  position: "top" | "bottom" | "left" | "right";
  children: ReactNode;
  delay?: boolean;
}

const calculatePosition = (position = "right") => {
  switch (position) {
    case "top":
      return {
        bottom: "100%",
        left: 0,
        right: 0,
      };
    case "bottom":
      return {
        top: "100%",
        left: 0,
        right: 0,
      };
    case "left":
      return {
        top: 0,
        right: "100%",
        bottom: 0,
      };
    case "right":
      return {
        top: 0,
        left: "100%",
        bottom: 0,
      };
    default:
      return { top: 0, left: "100%", bottom: 0 };
  }
};

const TooltipGOP: React.FC<TooltipProps> = ({ text, position, delay = false, children }) => {
  const [tooltipPosition, setTooltipPosition] = useState<{
    top?: number | string;
    right?: number | string;
    left?: number | string;
    bottom?: number | string;
  }>({ top: 0, right: 0 });
  const [isTooltipVisible, setTooltipVisible] = useState(true);

  const showTooltip = (e: MouseEvent<HTMLDivElement>) => {
    if (!isTooltipVisible) {
      const tooltipPosition = calculatePosition(position);

      setTooltipPosition(tooltipPosition);
      setTooltipVisible(true);
    }
  };
  const hideTooltip = () => {
    setTooltipVisible(false);
  };

  return (
    <div className="relative" onMouseOver={showTooltip} onMouseOut={hideTooltip}>
      {children}
      {isTooltipVisible && (
        <div className="absolute text-sm flex flex-row items-center pl-2.5" style={tooltipPosition}>
          <div className="triangle-left">
            <div className="flex px-3 py-1 bg-white border-r-4 rounded-md shadow-custom">{text}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TooltipGOP;

export type PositionType =
  | "right"
  | "top"
  | "left"
  | "bottom"
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "filter";

export const TooltipItemGOP = ({
  children,
  tooltipsText,
  tooltipsComponent,
  tooltipsReactNode,
  position,
  disable = false,
  className,
  cursor = true,
  textClassName,
  wrapperClassName,
  hoverClassName,
  delay = 0,
}: {
  children: ReactNode;
  tooltipsText: string;
  tooltipsComponent?: any;
  tooltipsReactNode?: ReactNode;
  position: PositionType;
  disable?: boolean;
  className?: string;
  textClassName?: string;
  wrapperClassName?: string;
  hoverClassName?: string;
  cursor?: boolean;
  delay?: number;
}) => {
  const isTop = position.includes("top");
  const isRight = position.includes("right");
  const isBottom = position.includes("bottom");
  const isLeft = position.includes("left");
  const filterPosition = position.includes("filter");

  const [isHover, hoverProps] = useHover({ mouseEnterDelayMS: delay, mouseLeaveDelayMS: delay });
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHover && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      let top = rect.top;
      let left = rect.left;

      if (isTop) {
        top = rect.top - 40; 
        left = rect.left + rect.width / 2;
      } else if (isBottom) {
        top = rect.bottom + 12; 
        left = rect.left + rect.width / 2;
      } else if (isLeft) {
        top = rect.top + rect.height / 2;
        left = rect.left - 12;
      } else if (isRight) {
        top = rect.top + rect.height / 2;
        left = rect.right + 12;
      }

      setTooltipPos({ top, left });
    } else {
      setTooltipPos(null);
    }
  }, [isHover, isTop, isBottom, isLeft, isRight]);

  useEffect(() => {
    return () => {
      if (delay > 0 && hoverProps) {
        setTimeout(() => {
          hoverProps.onMouseLeave();
        }, delay);
      }
    };
  }, [hoverProps, delay]);

  const tooltipContent = tooltipPos && !disable && (
    <div
      className={twMerge(
        `fixed z-[9999] rounded-md shadow-custom bg-white px-3.5 py-1 text-[14px] text-wrap`,
        isTop && `-translate-x-1/2`,
        isBottom && `-translate-x-1/2`,
        isLeft && `-translate-x-full -translate-y-1/2`,
        isRight && `-translate-y-1/2`,
        className
      )}
      style={{
        top: `${tooltipPos.top}px`,
        left: `${tooltipPos.left}px`,
      }}
    >
      <span
        className={`absolute -z-10 h-2 w-2 ${
          (isTop && `top-full left-1/2 -translate-x-1/2 -mt-[5px] rotate-45 rounded-sm border-l border-t border-light bg-white`) ||
          (isBottom && `bottom-full left-1/2 -translate-x-1/2 -mb-[5px] rotate-45 rounded-l-sm border-b border-r border-light bg-white`) ||
          (isLeft && `left-full top-1/2 -translate-y-1/2 -ml-[5px] rotate-45 rounded-sm border-r border-t border-light bg-white`) ||
          (isRight && `right-full top-1/2 -translate-y-1/2 -mr-[5px] rotate-45 rounded-r-sm border-b border-l border-light bg-white`)
        }`}
      ></span>
      {tooltipsComponent && <div className="flex flex-col" dangerouslySetInnerHTML={{ __html: tooltipsComponent }} /> ||
       tooltipsReactNode && <>{tooltipsReactNode}</> ||
       <span className={twMerge(textClassName ? textClassName : "whitespace-nowrap")}>{tooltipsText}</span>}
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className={twMerge(
        cursor ? "cursor-pointer" : "",
        "relative inline-block group",
        wrapperClassName,
        isHover ? hoverClassName : ""
      )}
      {...(hoverProps || {})}
    >
      {children}
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </div>
  );
};
